/**
 * Phase 17 — the pool's gateways ranked on the editor's LIVE formula (POST
 * preview, zero writes): debounced so a slider drag asks once, rows without
 * a metric dropped, the previous answer kept on screen while the next one
 * loads. Asked only while a FORMULA order or FORMULA weights is in use, or
 * (Phase 19) a gateway rule is on — its hits come back on each route.
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  cleanHourFormula,
  type DropGenGatewayRanking,
  type DropGenGatewayRankingPreview,
} from '@trafficpulse/shared';

import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { api } from '@/services/api';

import { dropGeneratorKeys } from '../../../../useDropGenerator';

export interface GatewayRankingArgs {
  countryKey: string;
  campaignWiserId: number | null;
  ranking: DropGenGatewayRanking;
  routeIds: number[];
  enabled: boolean;
}

export function useGatewayRanking({ countryKey, campaignWiserId, ranking, routeIds, enabled }: GatewayRankingArgs) {
  // Debounce ONE stable string (an object is new on every render).
  const liveKey = JSON.stringify({ formula: cleanHourFormula(ranking.formula), routeIds, rules: ranking.rules ?? [] });
  const bodyKey = useDebouncedValue(liveKey, 400);
  const body = useMemo(() => JSON.parse(bodyKey) as { formula: DropGenGatewayRanking['formula']; routeIds: number[] }, [bodyKey]);
  const query = useQuery({
    queryKey: [...dropGeneratorKeys.all, 'gateway-ranking', countryKey, campaignWiserId, bodyKey] as const,
    queryFn: async () => {
      const res = await api.post<{ data: DropGenGatewayRankingPreview }>(
        `/drop-generator/gateway-ranking/${countryKey}/${campaignWiserId}/preview`,
        body,
      );
      return res.data.data;
    },
    enabled: enabled && Boolean(countryKey && campaignWiserId) && body.routeIds.length > 0 && body.formula.groups.length > 0,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
  return { ...query, pending: liveKey !== bodyKey };
}

export type GatewayRankingQuery = ReturnType<typeof useGatewayRanking>;
